import { z } from 'zod';

const itemStartTimeSentConfig = z
  .object({
    referenceTimeType: z.literal('item-start-time'),
    timeOffsetMinutes: z.number().int(),
  })
  .loose();

const fixedTimeSentConfig = z
  .object({
    referenceTimeType: z.literal('fixed-time'),
    fixedTime: z
      .object({ hours: z.number().int(), minutes: z.number().int() })
      .loose(),
    daysBefore: z.number().int().nonnegative().optional(),
  })
  .loose();

/**
 * When the scheduler sends an automated message whose template has no sent-time
 * rule: a confirmation the day before at this UTC hour, a preparation this many
 * minutes before the service starts. Shared so Messaging settings can show it.
 */
export const DEFAULT_CONFIRMATION_SEND_UTC_HOUR = 19;
export const DEFAULT_PREPARATION_LEAD_MINUTES = 75;

/** The two automated messages the scheduler sends per driver / guide. */
export const autoScheduledMessageKindSchema = z.enum([
  'confirmation',
  'preparation',
]);
export type AutoScheduledMessageKind = z.infer<
  typeof autoScheduledMessageKindSchema
>;

export const sentTimeRuleConfigSchema = z.discriminatedUnion(
  'referenceTimeType',
  [itemStartTimeSentConfig, fixedTimeSentConfig]
);
export type SentTimeRuleConfig = z.infer<typeof sentTimeRuleConfigSchema>;

export const sentTimeRuleItemSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  config: sentTimeRuleConfigSchema,
});
export type SentTimeRuleItem = z.infer<typeof sentTimeRuleItemSchema>;

export const createSentTimeRuleInput = z.object({
  title: z.string().nullable().optional(),
  config: sentTimeRuleConfigSchema,
});
export type CreateSentTimeRuleInput = z.infer<typeof createSentTimeRuleInput>;

export const updateSentTimeRuleInput = z.object({
  id: z.uuid(),
  title: z.string().nullable().optional(),
  config: sentTimeRuleConfigSchema.optional(),
});
export type UpdateSentTimeRuleInput = z.infer<typeof updateSentTimeRuleInput>;

export const findSentTimeRulesInput = z.object({
  offset: z.number().int().nonnegative().optional(),
  limit: z.number().int().min(1).max(200).optional(),
  title: z.string().optional(),
});
export type FindSentTimeRulesInput = z.infer<typeof findSentTimeRulesInput>;

export const findSentTimeRulesResultSchema = z.object({
  data: z.array(sentTimeRuleItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});
export type FindSentTimeRulesResult = z.infer<
  typeof findSentTimeRulesResultSchema
>;

export const deleteSentTimeRuleInput = z.object({ id: z.uuid() });
export type DeleteSentTimeRuleInput = z.infer<typeof deleteSentTimeRuleInput>;

export const deleteSentTimeRuleResultSchema = z.object({ id: z.uuid() });
export type DeleteSentTimeRuleResult = z.infer<
  typeof deleteSentTimeRuleResultSchema
>;
